import { useEffect, useId, useState, type ChangeEvent, type InputHTMLAttributes, type KeyboardEvent, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { decimalSeparator, fmtDate, formatLocale, isISODate, parseDecimal, parseMoneyCents } from '@immoaktas/shared';
import { Icon } from './icons';
import { HelpTip, type GlossaryTerm } from './help';

type FieldBase = { label: string; hint?: ReactNode; error?: string | undefined; optional?: boolean; labelHidden?: boolean; className?: string; /** Glossary term: a "Was heißt das?" button right after the label. */ term?: GlossaryTerm };

/** Label above, hint or error below (docs/10 "Validierung am Feld": red border + icon + text, never colour alone). */
export function Field({ label, hint, error, optional, labelHidden, className = '', children, htmlFor, describedBy, term }: FieldBase & { children: ReactNode; htmlFor?: string; describedBy?: string }) {
  const { t } = useTranslation('common');
  const labelEl = (
    <label htmlFor={htmlFor} className={labelHidden ? 'sr-only' : undefined}>
      {label}
      {optional && <span className="optional"> ({t('ui.optional')})</span>}
    </label>
  );
  return (
    <div className={`field ${className}`}>
      {term && !labelHidden ? <span className="field-label-row">{labelEl}<HelpTip term={term} label={label} /></span> : labelEl}
      {children}
      {error ? <span className="err" id={describedBy} role="alert">{error}</span> : hint ? <span className="hint" id={describedBy}>{hint}</span> : null}
    </div>
  );
}

/** Text input with label. `adornment` renders a unit (e.g. €, m²) inside the field on the right. */
export function TextInput({ label, hint, error, optional, labelHidden, className, term, adornment, ...rest }: InputHTMLAttributes<HTMLInputElement> & FieldBase & { adornment?: ReactNode }) {
  const id = useId();
  const inputId = rest.id ?? id;
  const msgId = `${inputId}-msg`;
  const wrapped = !!(error || adornment);
  // The input keeps the same parent whether an error shows or not: switching between a wrapper and a bare input
  // re-mounted it, so typing into a field with an error kept only the first character and lost focus (Cowork 04.11.,
  // Haus-Assistent). Without adornment the slot is `display: contents`, the layout stays that of a bare input.
  return (
    <Field label={label} hint={hint} error={error} optional={optional} labelHidden={labelHidden} className={className} term={term} htmlFor={inputId} describedBy={msgId}>
      <span className={wrapped ? 'input-wrap' : 'input-slot'}>
        <input id={inputId} className={`input ${error ? 'invalid' : ''}`} aria-invalid={!!error || undefined} aria-describedby={error || hint ? msgId : undefined} {...rest} />
        {wrapped && <span className={`adorn ${error ? 'invalid' : ''}`}>{error ? <Icon name="alert" size={18} strokeWidth={2} /> : adornment}</span>}
      </span>
    </Field>
  );
}

export function TextArea({ label, hint, error, optional, labelHidden, className, term, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & FieldBase) {
  const id = useId();
  const msgId = `${id}-msg`;
  return (
    <Field label={label} hint={hint} error={error} optional={optional} labelHidden={labelHidden} className={className} term={term} htmlFor={id} describedBy={msgId}>
      <textarea id={id} className={`input ${error ? 'invalid' : ''}`} aria-invalid={!!error || undefined} aria-describedby={error || hint ? msgId : undefined} {...rest} />
    </Field>
  );
}

export type Option = { value: string; label: string; disabled?: boolean };
export function SelectInput({ label, hint, error, optional, labelHidden, className, term, options, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & FieldBase & { options: Option[] }) {
  const id = useId();
  const msgId = `${id}-msg`;
  return (
    <Field label={label} hint={hint} error={error} optional={optional} labelHidden={labelHidden} className={className} term={term} htmlFor={id} describedBy={msgId}>
      <select id={id} className={`input ${error ? 'invalid' : ''}`} aria-invalid={!!error || undefined} aria-describedby={error || hint ? msgId : undefined} {...rest}>
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

/** Cents as input text in the UI locale with grouping: "2.384,10" (de) / "2,384.10" (en). */
const centsText = (c: number | null) => (c == null ? '' : new Intl.NumberFormat(formatLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(c / 100));

/** Typed number in the UI locale, tolerant of the other separator style (shared parseDecimal). Null for empty or invalid input. */
export function parseDecimalInput(text: string, dec = decimalSeparator()): number | null {
  return parseDecimal(text, dec);
}
/** Typed money → integer cents (shared parseMoneyCents: exact, at most 2 decimals, otherwise null). */
export function parseMoneyInput(text: string, dec = decimalSeparator()): number | null {
  return parseMoneyCents(text, dec);
}

const dateSep = () => (formatLocale().startsWith('de') ? '.' : '/');
/** '2025-12-31' → "31.12.2025" (en "31/12/2025"); '' for anything else. */
export const isoToDateText = (iso: string | null | undefined) =>
  iso && isISODate(iso) ? [iso.slice(8, 10), iso.slice(5, 7), iso.slice(0, 4)].join(dateSep()) : '';
const SEP = /[./-]/;
/** Two-digit year: 20yy, unless that is more than 10 years ahead (then 19yy, e.g. a birth date "12.05.58"). */
export function fullYear(yy: string, now = new Date().getFullYear()) {
  if (yy.length !== 2) return yy;
  const y = 2000 + Number(yy);
  return String(y > now + 10 ? y - 100 : y);
}
/**
 * "31.12.2025" / "31/12/2025" / "31122025", short forms with typed separators ("1.3.2025", "01.03.25" → 2025) or a
 * pasted '2025-12-31' → '2025-12-31'; null while incomplete or no calendar date (31.02.).
 */
export function dateTextToIso(text: string): string | null {
  const trimmed = text.trim();
  if (isISODate(trimmed)) return trimmed;
  if (SEP.test(trimmed)) {
    const m = /^(\d{1,2})[./-](\d{1,2})[./-](\d{2}|\d{4})$/.exec(trimmed);
    if (!m) return null;
    const iso = `${fullYear(m[3]!)}-${m[2]!.padStart(2, '0')}-${m[1]!.padStart(2, '0')}`;
    return isISODate(iso) ? iso : null;
  }
  const d = text.replace(/\D/g, '');
  if (d.length !== 8) return null;
  const iso = `${d.slice(4, 8)}-${d.slice(2, 4)}-${d.slice(0, 2)}`;
  return isISODate(iso) ? iso : null;
}
/**
 * While typing: digits only get the separators inserted (3112 → 31.12 → 31.12.2025, same as the app's DateField);
 * separators the person types are kept ("1.3." stays), so short forms like "1.3.25" work. Day and month take at most
 * two digits, the year four; extra digits move on to the next part.
 */
export function maskDateText(input: string) {
  if (isISODate(input.trim())) return isoToDateText(input.trim());
  const clean = input.replace(/[^\d./-]/g, '');
  if (!SEP.test(clean)) {
    const d = clean.slice(0, 8);
    return [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean).join(dateSep());
  }
  const max = [2, 2, 4];
  const tokens = clean.split(SEP);
  const trailing = tokens[tokens.length - 1] === '';
  const parts: string[] = [];
  for (const tok of tokens.filter(Boolean)) {
    let rest = tok;
    while (rest && parts.length < 3) {
      const take = max[parts.length]!;
      parts.push(rest.slice(0, take));
      rest = rest.slice(take);
    }
  }
  return parts.join(dateSep()) + (trailing && parts.length > 0 && parts.length < 3 ? dateSep() : '');
}

type DateInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange' | 'min' | 'max'> & FieldBase & {
  /** 'YYYY-MM-DD' or '' */
  value: string;
  /** Called like a native date input: `e.target.value` is a valid date as 'YYYY-MM-DD' (also outside min/max, which the field marks), else ''. */
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  min?: string;
  max?: string;
};

/**
 * Date field in the local order (TT.MM.JJJJ / DD/MM/YYYY), ported from the app's DateField: digits only, the
 * separators are typed for you, a pasted ISO date works too. No native picker (it differs per browser and hides the
 * format). Reports like a native date input (`e.target.value` ISO or ''), shows "invalid" and range errors itself
 * after leaving the field.
 */
export function DateInput({ label, hint, error, optional, labelHidden, className, term, value, onChange, onBlur, min, max, ...rest }: DateInputProps) {
  const { t } = useTranslation('common');
  const id = useId();
  const inputId = rest.id ?? id;
  const msgId = `${inputId}-msg`;
  const [text, setText] = useState(() => isoToDateText(value));
  const [blurred, setBlurred] = useState(false);
  // follow outside changes (AI fields, a reset) without wiping a half-typed date
  useEffect(() => {
    if ((dateTextToIso(text) ?? '') !== value) setText(isoToDateText(value));
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  const iso = dateTextToIso(text);
  const own = blurred && text && !iso ? t('ui.date.invalid')
    : iso && max && iso > max ? t('ui.date.tooLate', { date: fmtDate(max) })
    : iso && min && iso < min ? t('ui.date.tooEarly', { date: fmtDate(min) })
    : undefined;
  const shown = error ?? own;
  return (
    <Field label={label} hint={hint} error={shown} optional={optional} labelHidden={labelHidden} className={className} term={term} htmlFor={inputId} describedBy={msgId}>
      <span className="input-wrap">
        <input
          {...rest}
          id={inputId}
          className={`input date-text ${shown ? 'invalid' : ''}`}
          inputMode="numeric"
          autoComplete="off"
          maxLength={10}
          placeholder={rest.placeholder ?? t('ui.date.placeholder')}
          value={text}
          aria-invalid={!!shown || undefined}
          aria-describedby={[shown || hint ? msgId : '', `${inputId}-fmt`].filter(Boolean).join(' ')}
          onChange={(e) => {
            const m = maskDateText(e.target.value);
            setText(m);
            const next = dateTextToIso(m) ?? '';
            onChange?.({ ...e, target: { ...e.target, value: next }, currentTarget: { ...e.currentTarget, value: next } } as ChangeEvent<HTMLInputElement>);
          }}
          onBlur={(e) => { setBlurred(true); if (iso) setText(isoToDateText(iso)); onBlur?.(e); }}
        />
        <span className={`adorn ${shown ? 'invalid' : ''}`}>{shown ? <Icon name="alert" size={18} strokeWidth={2} /> : <Icon name="deadlines" size={18} />}</span>
        <span className="sr-only" id={`${inputId}-fmt`}>{t('ui.date.format', { format: t('ui.date.placeholder') })}</span>
      </span>
    </Field>
  );
}

/** '2025-03' → "03.2025" (en "03/2025"); '' for anything else. */
export const isoToMonthText = (ym: string | null | undefined) => (ym && /^\d{4}-(0[1-9]|1[0-2])$/.test(ym) ? `${ym.slice(5, 7)}${dateSep()}${ym.slice(0, 4)}` : '');
/** "03.2025" / "3.2025" / "032025" or a pasted '2025-03' → '2025-03'; null while incomplete or no month. */
export function monthTextToIso(text: string): string | null {
  const s = text.trim();
  const iso = /^(\d{4})-(\d{2})$/.exec(s);
  if (iso) return Number(iso[2]) >= 1 && Number(iso[2]) <= 12 ? s : null;
  const parts = /^(\d{1,2})[./-](\d{4})$/.exec(s) ?? /^(\d{2})(\d{4})$/.exec(s);
  if (!parts) return null;
  const mm = Number(parts[1]);
  return mm >= 1 && mm <= 12 ? `${parts[2]}-${String(mm).padStart(2, '0')}` : null;
}
/** While typing: 032025 → 03.2025; a typed separator is kept ("3." stays). */
export function maskMonthText(input: string) {
  if (/^\d{4}-\d{2}$/.test(input.trim())) return isoToMonthText(input.trim());
  const clean = input.replace(/[^\d./-]/g, '');
  const i = clean.search(SEP);
  if (i < 0) {
    const d = clean.slice(0, 6);
    return [d.slice(0, 2), d.slice(2, 6)].filter(Boolean).join(dateSep());
  }
  const mm = clean.slice(0, i).slice(0, 2);
  const yyyy = clean.slice(i + 1).replace(/\D/g, '').slice(0, 4);
  return mm ? `${mm}${dateSep()}${yyyy}` : yyyy;
}

type MonthInputProps = Omit<DateInputProps, 'min' | 'max'>;
/** Month field MM.JJJJ (instead of the browser's month picker, which Safari and Firefox lack): reports 'YYYY-MM' or ''. */
export function MonthInput({ label, hint, error, optional, labelHidden, className, term, value, onChange, onBlur, ...rest }: MonthInputProps) {
  const { t } = useTranslation('common');
  const id = useId();
  const inputId = rest.id ?? id;
  const msgId = `${inputId}-msg`;
  const [text, setText] = useState(() => isoToMonthText(value));
  const [blurred, setBlurred] = useState(false);
  useEffect(() => {
    if ((monthTextToIso(text) ?? '') !== value) setText(isoToMonthText(value));
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  const iso = monthTextToIso(text);
  const shown = error ?? (blurred && text && !iso ? t('ui.month.invalid') : undefined);
  return (
    <Field label={label} hint={hint} error={shown} optional={optional} labelHidden={labelHidden} className={className} term={term} htmlFor={inputId} describedBy={msgId}>
      <input
        {...rest}
        id={inputId}
        className={`input date-text ${shown ? 'invalid' : ''}`}
        inputMode="numeric"
        autoComplete="off"
        maxLength={7}
        placeholder={rest.placeholder ?? t('ui.month.placeholder')}
        value={text}
        aria-invalid={!!shown || undefined}
        aria-describedby={[shown || hint ? msgId : '', `${inputId}-fmt`].filter(Boolean).join(' ')}
        onChange={(e) => {
          const m = maskMonthText(e.target.value);
          setText(m);
          const next = monthTextToIso(m) ?? '';
          onChange?.({ ...e, target: { ...e.target, value: next }, currentTarget: { ...e.currentTarget, value: next } } as ChangeEvent<HTMLInputElement>);
        }}
        onBlur={(e) => { setBlurred(true); if (iso) setText(isoToMonthText(iso)); onBlur?.(e); }}
      />
      <span className="sr-only" id={`${inputId}-fmt`}>{t('ui.month.format', { format: t('ui.month.placeholder') })}</span>
    </Field>
  );
}

/** Money input: shows "1.234,56" (en "1,234.56"), value is integer cents (docs/08: Euro immer 1.234,56 €). */
export function MoneyInput({ label, valueCents, onChange, hint, error, required, disabled, optional, labelHidden, className, term }: { label: string; valueCents: number | null; onChange: (c: number | null) => void; hint?: ReactNode; error?: string; required?: boolean; disabled?: boolean; optional?: boolean; labelHidden?: boolean; className?: string; term?: GlossaryTerm }) {
  const id = useId();
  const msgId = `${id}-msg`;
  const { t } = useTranslation('common');
  const [text, setText] = useState(centsText(valueCents));
  // Unparseable text ("12,3x", "1,005") keeps the text and reports null; the field says so once the user leaves it.
  const [showInvalid, setShowInvalid] = useState(false);
  const invalid = text.trim() !== '' && parseMoneyInput(text) == null;
  const shownError = error ?? (showInvalid && invalid ? t('ui.money.invalid') : undefined);
  useEffect(() => {
    if (parseMoneyInput(text) !== valueCents) setText(centsText(valueCents));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueCents]);
  return (
    <Field label={label} hint={hint} error={shownError} optional={optional} labelHidden={labelHidden} className={className} term={term} htmlFor={id} describedBy={msgId}>
      <span className="input-wrap">
        <input id={id} className={`input money ${shownError ? 'invalid' : ''}`} inputMode="decimal" value={text} required={required} disabled={disabled} placeholder={centsText(0)} aria-invalid={!!shownError || undefined} aria-describedby={shownError || hint ? msgId : undefined} onChange={(e) => { setText(e.target.value); onChange(parseMoneyInput(e.target.value)); if (showInvalid && parseMoneyInput(e.target.value) != null) setShowInvalid(false); }} onBlur={() => { if (invalid) setShowInvalid(true); else if (valueCents != null) setText(centsText(valueCents)); }} />
        <span className={`adorn ${shownError ? 'invalid' : ''}`}>{shownError ? <Icon name="alert" size={18} strokeWidth={2} /> : '€'}</span>
      </span>
    </Field>
  );
}

export function Check({ label, checked, onChange, disabled, hint }: { label: ReactNode; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; hint?: ReactNode }) {
  return (
    <label className="check">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span>
        {label}
        {hint && <span className="small muted" style={{ display: 'block' }}>{hint}</span>}
      </span>
    </label>
  );
}

/** Search field with leading magnifier; label is visually hidden unless `showLabel`. */
export function SearchInput({ label, showLabel, className = '', ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: string; showLabel?: boolean }) {
  const id = useId();
  return (
    <div className={`field ${className}`}>
      <label htmlFor={id} className={showLabel ? undefined : 'sr-only'}>{label}</label>
      <span className="search-field">
        <Icon name="search" size={16} strokeWidth={1.8} />
        <input id={id} type="search" className="input" {...rest} />
      </span>
    </div>
  );
}

/** Filter row above lists (design/Liste): small muted labels, fields aligned at the bottom. */
export const FilterBar = ({ children, label }: { children: ReactNode; label?: string }) => (
  <div className="filters" role={label ? 'search' : undefined} aria-label={label}>
    {children}
  </div>
);

/** Setting row: title + explanation left, control right (design/Einstellungen). */
export const SettingRow = ({ title, text, children }: { title: ReactNode; text?: ReactNode; children: ReactNode }) => (
  <div className="setting-row">
    <span className="setting-text">
      <strong>{title}</strong>
      {text && <span>{text}</span>}
    </span>
    <div>{children}</div>
  </div>
);

/** Pill choices (design/Onboarding). Single choice = radiogroup, `multiple` = toggle buttons. */
export function ChoiceChips<V extends string>({ label, options, value, onChange, multiple }: { label: string; options: { value: V; label: string }[]; value: V | V[]; onChange: (v: V | V[]) => void; multiple?: boolean }) {
  const id = useId();
  const selected = new Set(Array.isArray(value) ? value : [value]);
  const toggle = (v: V) => {
    if (!multiple) return onChange(v);
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(options.map((o) => o.value).filter((x) => next.has(x)));
  };
  return (
    <div className="stack tight" style={{ gap: 10 }}>
      <span id={id} className="strong">{label}</span>
      <div className="chips" role={multiple ? 'group' : 'radiogroup'} aria-labelledby={id}>
        {options.map((o) =>
          multiple ? (
            <button key={o.value} type="button" className="chip" aria-pressed={selected.has(o.value)} onClick={() => toggle(o.value)}>{o.label}</button>
          ) : (
            <button key={o.value} type="button" className="chip" role="radio" aria-checked={selected.has(o.value)} onClick={() => toggle(o.value)}>{o.label}</button>
          ),
        )}
      </div>
    </div>
  );
}

/** Segmented control (design/Main "Haus | Liste"). */
/** Switch between a few views (Haus / Liste). Radio group: one tab stop, arrow keys move the choice. */
export function Segmented<K extends string>({ label, options, value, onChange }: { label: string; options: { value: K; label: string }[]; value: K; onChange: (k: K) => void }) {
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const i = options.findIndex((o) => o.value === value);
    const next = options[(i + step + options.length) % options.length];
    if (!next) return;
    onChange(next.value);
    const box = e.currentTarget;
    requestAnimationFrame(() => box.querySelector<HTMLElement>('[aria-checked="true"]')?.focus());
  };
  const selected = options.some((o) => o.value === value);
  return (
    <div className="segmented" role="radiogroup" aria-label={label} onKeyDown={onKey}>
      {options.map((o, i) => (
        <button key={o.value} type="button" role="radio" aria-checked={o.value === value} tabIndex={o.value === value || (!selected && i === 0) ? 0 : -1} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}
